import React from "react";
import { createRoot } from "react-dom/client";

import RepaymentCalculator from "./RepaymentCalculator";
import { __ } from "@wordpress/i18n";

// Important: Import styles directly in JS for HMR to work with styles
import "./styles/style.scss";

// Set up WordPress i18n
if (typeof wp !== "undefined" && wp.i18n && typeof window.__ === "undefined") {
  window.__ = wp.i18n.__;
}

// Store references to the roots for HMR
const rootsMap = new Map();

// Parse APR tiers from dataset
function parseAprTiers(data) {
  let aprTiers;

  try {
    if (data.aprTiers) {
      aprTiers = JSON.parse(data.aprTiers);
      if (!Array.isArray(aprTiers)) {
        aprTiers = null;
      }
    } else if (data.aprValue) {
      const aprValue = parseFloat(data.aprValue);
      if (!isNaN(aprValue)) {
        aprTiers = [{ limit: 10000000, apr: aprValue }];
      }
    }
  } catch (error) {
    console.error("Error parsing APR tiers:", error);

    const aprValue = parseFloat(data.aprValue);
    if (!isNaN(aprValue)) {
      aprTiers = [{ limit: 10000000, apr: aprValue }];
    }
  }

  return aprTiers;
}

// Extract props from data attributes
function getCalculatorProps(data) {
  return {
    amountMin: parseFloat(data.amountMin) || 1000,
    amountMax: parseFloat(data.amountMax) || 10000,
    amountStep: parseFloat(data.amountStep) || 100,
    amountDefault: parseFloat(data.amountDefault) || 5000,
    monthsMin: parseFloat(data.monthsMin) || 6,
    monthsMax: parseFloat(data.monthsMax) || 60,
    monthsStep: parseFloat(data.monthsStep) || 6,
    monthsDefault: parseFloat(data.monthsDefault) || 24,
    weeksMin: parseFloat(data.weeksMin),
    weeksMax: parseFloat(data.weeksMax),
    weeksStep: parseFloat(data.weeksStep),
    weeksDefault: parseFloat(data.weeksDefault),
    aprTiers: parseAprTiers(data),
  };
}

// Mount a calculator to a container
function mountCalculator(container) {
  try {
    const props = getCalculatorProps(container.dataset);

    // Get or create a root for this container
    let root = rootsMap.get(container);
    if (!root) {
      root = createRoot(container);
      rootsMap.set(container, root);
    }

    root.render(<RepaymentCalculator {...props} />);
    container.dataset.mounted = "true";
  } catch (error) {
    console.error("Failed to mount calculator:", error);
  }
}

// Find and mount all calculator instances in the document
function mountCalculators() {
  const containers = document.querySelectorAll(".mrc-react-calculator");

  if (containers.length === 0) {
    return false;
  }

  containers.forEach(mountCalculator);
  return true;
}

// Set up polling mechanism to find elements after DOM changes
function setupPolling() {
  let checkCount = 0;
  const maxChecks = 20;

  // Try immediate mount
  mountCalculators();

  const intervalId = setInterval(() => {
    checkCount++;
    const success = mountCalculators();

    if (success || checkCount >= maxChecks) {
      clearInterval(intervalId);
    }
  }, 500);
}

// Set up mutation observer to detect DOM changes
function setupMutationObserver() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const hasCalculator =
              node.classList?.contains("mrc-react-calculator") ||
              node.querySelectorAll?.(".mrc-react-calculator").length > 0;

            if (hasCalculator) {
              mountCalculators();
              break;
            }
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Initialize everything
function initialize() {
  setupPolling();
  setupMutationObserver();
}

// Handle Elementor integration
if (window.elementorFrontend) {
  window.addEventListener("elementor/frontend/init", () => {
    elementorFrontend.hooks.addAction(
      "frontend/element_ready/repayment_calculator.default",
      (element) => {
        const containers = element.find(".mrc-react-calculator");

        if (containers.length > 0) {
          Array.from(containers).forEach((container) => {
            mountCalculator(
              container instanceof jQuery ? container[0] : container
            );
          });
        }
      }
    );
  });
}

// Initialize on DOM ready or immediately if already loaded
document.addEventListener("DOMContentLoaded", initialize);
if (
  document.readyState === "interactive" ||
  document.readyState === "complete"
) {
  console.log("Initializing...");
  initialize();
}
